/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Dialog, IconButton, Stack, Tooltip, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StyledButton from '../buttons/StyledButton';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEffect, useState } from 'react';
import StyledTextField from '../textField/StyledTextField';
import { SSCCProps, SsccType } from '../../datastore/ssccs';
import dayjs from 'dayjs';
import NewSsccForm, { NewSsccModel } from './NewSSCCForm';
import { LocationProps, UnknownLocation, LocationType } from '../../datastore/locations';
import { useAlert, useCurrentUser, useDatastore, useIsOnline, usePhasedModelGate } from '../../hooks';
import DataLoadingNotice from '../DataLoadingNotice';
import { buildSSCC, buildReceiptEvent, SsccStatus } from '../../common';
import { sumQuantity } from '../../common/helperFunctions/sscc';
import { SsccPrintProps } from '../../../../Common/Types/types';
import { yearMonthDateFormat } from '../../common/dateTimes';
import { printSscc } from '../../common/helperFunctions/print';
import { captureError } from '../../common/sentry';

interface AssignToSSCCFormProps {
  open: boolean;
  onSubmit: (ssccChoice: string) => void;
  onClose: () => void;
  onBack: () => void;
  isLargeItem: boolean;
  poLocation: LocationProps | undefined;
}

export default function AssignToSSCCForm({
  open,
  onSubmit,
  onClose,
  onBack,
  isLargeItem,
  poLocation
}: AssignToSSCCFormProps) {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SSCCProps[]>([]);
  const [ssccChoice, setSSCCChoice] = useState<string | null>();
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [userLocation, setUserLocation] = useState<LocationProps>();
  const [newSSCCId, setNewSSCCId] = useState<string | null>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [hasCreatedSSCC, setHasCreatedSSCC] = useState(false);
  const [isSsccPrintLoading, setIsSsccPrintLoading] = useState(false);

  const store = useDatastore();
  const theme = useTheme();
  const alert = useAlert();
  const user = useCurrentUser();
  const { isOnline } = useIsOnline();
  const { loading: ssccLoading } = usePhasedModelGate('SSCC');
  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    clearTimeout(timer);
    const timeoutId = setTimeout(() => getData(), 500);
    setTimer(timeoutId);

    return () => {
      clearTimeout(timer);
    };
  }, [search, newSSCCId]);

  async function getData() {
    try {
      const isNotUnknownMainLocation =
        poLocation?.type === LocationType.mainLocation && poLocation.name !== UnknownLocation.Name;
      const isSubLocation = poLocation?.type === LocationType.subLocation;
      const ssccResults = await store.ssccs.getList(search);
      const locationResults = await store.locations.getList();
      const userDetails = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);

      if (ssccResults && locationResults) {
        const allLocations = locationResults.filter((l) => !l.isDisabled);
        setLocations(allLocations);

        const filterSSCCs = (sscc: SSCCProps) =>
          !sscc.lock &&
          userDetails.location?.id === sscc.originLocationId &&
          !(sscc.staged || sscc.manifested || sscc.inTransit || sscc.approaching || sscc.delivered);

        let filteredSSCCs = [];

        if (!search) {
          if (isNotUnknownMainLocation) {
            const hasChildren = allLocations.some((l) => l.parentLocationId === poLocation.id);

            if (hasChildren) {
              // For parent locations, only show SSCCs destined for the parent
              filteredSSCCs = ssccResults.filter(
                (sscc) => sscc.destinationLocationId === poLocation.id && filterSSCCs(sscc)
              );
            } else {
              // For parent locations without children, or if it's a child location
              const relevantLocationIds = allLocations
                .filter((l) => l.parentLocationId === poLocation.id || l.id === poLocation.id)
                .map((l) => l.id);

              filteredSSCCs = ssccResults.filter(
                (sscc) => relevantLocationIds.includes(sscc.destinationLocationId) && filterSSCCs(sscc)
              );
            }
          } else if (isSubLocation) {
            // For sub-locations, include SSCCs destined for this sub-location or its parent
            filteredSSCCs = ssccResults.filter(
              (sscc) =>
                (poLocation?.id === sscc.destinationLocationId ||
                  poLocation?.parentLocationId === sscc.destinationLocationId) &&
                filterSSCCs(sscc)
            );
          } else {
            // Unknown location so no filtering is required.
            filteredSSCCs = ssccResults.filter(filterSSCCs);
          }
          setSearchResults(filteredSSCCs);
        } else {
          setSearchResults(ssccResults.filter(filterSSCCs));
        }
      }

      if (userDetails.location) {
        setUserLocation(userDetails?.location);
      }
    } catch (error) {
      console.error('Error retrieving AssignToSSCC Data: ', error);
      captureError(error, 'assignToSSCCForm-getData');
    }
  }

  function onSearchChange(value: string) {
    setSearch(value);
  }

  function onSubmitForm() {
    if (ssccChoice) {
      onSubmit(ssccChoice);
    } else {
      alert('Please select an SSCC', 'warning');
    }
  }

  async function onCreateNew(data: NewSsccModel) {
    if (!userLocation) {
      alert('You must set a location in `settings` before you can create an SSCC', 'warning');
      return;
    }

    setIsSsccPrintLoading(true);

    const newSSCC = buildSSCC({
      locationAlias: userLocation.alias,
      destinationLocationId: data.site,
      originLocationId: userLocation.id,
      isLargeItem: isLargeItem,
      isTransportBooking: false,
      transportBookingDestination: null,
      height: data.height,
      width: data.width,
      depth: data.depth,
      weight: undefined,
      ssccStatus: SsccStatus.open,
      ssccType: SsccType.standard,
      stagingLocation: undefined,
      nickname: data.nickname
    });

    const newSite = buildReceiptEvent({
      name: locations.find((location) => location.id === data.site)?.name || '',
      site: locations.find((location) => location.id === data.site)?.alias || ''
    });

    if (!newSSCC) {
      alert('An error has occured whilst creating the SSCC, try again.', 'warning');
      return;
    }

    newSSCC.receiptedEvents.push(newSite);

    const ssccPrint: Partial<SsccPrintProps> = {
      nickname: newSSCC.nickname || '',
      createdAt: dayjs().format(yearMonthDateFormat),
      ssccId: newSSCC.id,
      destinationAlias: locations.find((location) => location.id === data.site)?.alias || '',
      hasDangerousGoods: false
    };

    try {
      await store.ssccs.create(newSSCC);
      alert('Created SSCC', 'success');
      const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);
      if (!userPrinter) {
        alert('No printer found, please set a printer and reprint from Manage SSCC screen', 'warning');
        return;
      }
      await printSscc([ssccPrint], userPrinter?.printerIP, userPrinter?.printerPort, 'ftl_ssccOpen_print');
    } catch (error) {
      alert('An error occured when creating the SSCC', 'error');
      console.error(error);
      captureError(error, 'assignToSSCCForm-onCreateNew');
    }

    setIsSsccPrintLoading(false);
    setNewSSCCId(newSSCC.id);
    setSSCCChoice(newSSCC.id);
    setSearch('');
    setHasCreatedSSCC(true);
    setOpenNewModal(false);
  }

  const orderedResults = searchResults.sort((a, b) => {
    const dateA = dayjs(a.open).unix();
    const dateB = dayjs(b.open).unix();
    return dateA > dateB ? -1 : 1;
  });

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <Stack spacing={0}>
          {/* Header */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <IconButton onClick={onBack} size="small" sx={{ marginRight: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                <InventoryIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                <Typography variant="h6" fontWeight={600}>
                  Assign to SSCC
                </Typography>
              </Stack>
              <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ padding: 3, maxHeight: '70vh', overflowY: 'auto', backgroundColor: 'grey.50' }}>
            <Stack spacing={3}>
              {/* Search Box */}
              <StyledTextField
                value={search}
                onChange={onSearchChange}
                startIcon={<SearchIcon />}
                placeholder="Search by SSCC or Site"
                disabled={ssccLoading}
              />

              {/* Create New SSCC Button */}
              <StyledButton
                variant="outlined"
                disabled={hasCreatedSSCC}
                fullWidth
                onClick={() => {
                  setOpenNewModal(true);
                }}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  borderRadius: 2,
                  paddingY: 1,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  '&:hover': {
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    borderColor: 'grey.400',
                    backgroundColor: 'grey.50'
                  }
                }}>
                Create New SSCC
              </StyledButton>
              {/* SSCC List */}
              {ssccLoading ? (
                <DataLoadingNotice label="SSCC" />
              ) : (
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    padding: 2.5,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    {search.length > 0 ? 'Search Results' : 'Available SSCCs'}
                  </Typography>

                  <Stack spacing={1.5}>
                    {(search.length > 0 ? searchResults : orderedResults).slice(0, 5).length > 0 ? (
                      (search.length > 0 ? searchResults : orderedResults).slice(0, 5).map((sscc) => {
                        const itemCount = sscc?.receiptedEvents?.reduce((acc, site) => {
                          const siteTotal = site?.packages?.reduce(sumQuantity, 0) || 0;
                          return acc + siteTotal;
                        }, 0);
                        const isSelected = ssccChoice === sscc.id;

                        return (
                          <Box
                            key={sscc.id}
                            onClick={() => setSSCCChoice(sscc.id)}
                            sx={{
                              padding: 2,
                              borderRadius: 1.5,
                              border: `2px solid ${isSelected ? theme.palette.success.main : theme.palette.divider}`,
                              backgroundColor: isSelected ? 'success.lighter' : 'grey.50',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: isSelected ? theme.palette.success.main : 'grey.400',
                                backgroundColor: isSelected ? 'success.lighter' : 'grey.100'
                              }
                            }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              {isSelected && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />}
                              <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {sscc.id}
                                  </Typography>
                                  {sscc.nickname && (
                                    <Chip label={sscc.nickname} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {locations.find((location) => location.id === sscc.destinationLocationId)?.alias}
                                </Typography>
                              </Box>
                              <Chip
                                label={`${itemCount} items`}
                                size="small"
                                sx={{ backgroundColor: 'white', fontWeight: 500 }}
                              />
                            </Stack>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          padding: 3,
                          textAlign: 'center',
                          color: 'text.secondary'
                        }}>
                        <Typography variant="body2">
                          {search.length > 0 ? 'No search results found' : 'No available SSCCs'}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
          {/* Footer */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: 'white',
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
            }}>
            {isOnline ? (
              <StyledButton
                variant="contained"
                onClick={onSubmitForm}
                fullWidth
                startIcon={<LocalShippingIcon />}
                sx={{
                  paddingY: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2
                }}>
                Complete + Print
              </StyledButton>
            ) : (
              <Tooltip title="You are offline. This feature is not available when offline." enterTouchDelay={0}>
                <span style={{ width: '100%' }}>
                  <StyledButton
                    variant="contained"
                    disabled
                    fullWidth
                    startIcon={<LocalShippingIcon />}
                    sx={{
                      paddingY: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}>
                    Complete + Print
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </Dialog>
      {openNewModal && (
        <NewSsccForm
          open={openNewModal}
          isLoading={isSsccPrintLoading}
          onClose={() => setOpenNewModal(false)}
          onSubmit={onCreateNew}
          sites={locations
            .map((location) => ({ name: location.name, value: location.id, type: location.type }))
            .filter((location) => location.name !== UnknownLocation.Name)}
          prePopulateSite
          originLocation={poLocation}
        />
      )}
    </>
  );
}
